/**
 * API Interceptors
 * Request, response, and error interceptors for API client
 */

import type { ApiRequestConfig, ApiResponse } from './types';
import {
  AuthenticationError as AuthenticationErrorClass,
  AuthorizationError as AuthorizationErrorClass,
  ValidationError as ValidationErrorClass,
  NotFoundError as NotFoundErrorClass,
  RateLimitError as RateLimitErrorClass,
  ApiError as ApiErrorClass,
} from './types';

/**
 * Authentication Request Interceptor
 * Adds authorization token to requests
 */
export function authenticationInterceptor(config: ApiRequestConfig): ApiRequestConfig {
  // Get token from localStorage (Zustand persist stores it under 'auth-store' key)
  try {
    const authStoreStr = localStorage.getItem('auth-store');
    if (authStoreStr) {
      const authStore = JSON.parse(authStoreStr) as { state?: { accessToken?: string } };
      const token = authStore.state?.accessToken;

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
    }
  } catch {
    // Silently fail if localStorage parsing fails
  }

  return config;
}

/**
 * Request ID Interceptor
 * Adds unique request ID for tracking
 */
export function requestIdInterceptor(config: ApiRequestConfig): ApiRequestConfig {
  const requestId = generateRequestId();

  config.headers = {
    ...config.headers,
    'X-Request-ID': requestId,
  };

  return config;
}

/**
 * Custom Headers Interceptor
 * Adds custom application headers
 */
export function customHeadersInterceptor(config: ApiRequestConfig): ApiRequestConfig {
  const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) || '1.0.0';

  config.headers = {
    ...config.headers,
    'X-App-Version': appVersion,
    'X-Client-Type': 'web',
    'X-Requested-With': 'XMLHttpRequest',
  };

  return config;
}

/**
 * Response Normalization Interceptor
 * Normalizes response data structure
 */
export function responseNormalizationInterceptor<T>(response: ApiResponse<T>): ApiResponse<T> {
  // Normalize timestamps if present
  if (typeof response.data === 'object' && response.data !== null) {
    const normalized = normalizeTimestamps(response.data);
    return {
      ...response,
      data: normalized as T,
    };
  }

  return response;
}

/**
 * Error Status Code Interceptor
 * Converts HTTP status codes to specific error types
 */
export function errorStatusInterceptor(error: Error): Error {
  if (!(error instanceof ApiErrorClass)) {
    return error;
  }

  switch (error.statusCode) {
    case 400:
      if (error.details?.errors) {
        return new ValidationErrorClass(error.details.errors, error.details);
      }
      return error;

    case 401:
      return new AuthenticationErrorClass(error.message, error.details);

    case 403:
      return new AuthorizationErrorClass(error.message, error.details);

    case 404:
      return new NotFoundErrorClass('Resource', error.details);

    case 429:
      return new RateLimitErrorClass(parseRetryAfterHeader(), error.details);

    default:
      return error;
  }
}

/**
 * Token Refresh Interceptor
 * Handles token refresh on 401 responses
 * Works with Zustand auth store for token management
 */
export async function tokenRefreshInterceptor(error: Error): Promise<Error> {
  if (!(error instanceof AuthenticationErrorClass)) {
    return error;
  }

  try {
    // Get auth store state from localStorage (Zustand persist)
    const authStoreStr = localStorage.getItem('auth-store');
    if (!authStoreStr) {
      redirectToLogin();
      return error;
    }

    const authStore = JSON.parse(authStoreStr) as {
      state?: { refreshToken?: string; accessToken?: string };
    };
    const refreshToken = authStore.state?.refreshToken;

    if (!refreshToken) {
      // Redirect to login if no refresh token
      redirectToLogin();
      return error;
    }

    // Attempt to refresh token
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      // Refresh failed, clear tokens and redirect to login
      clearAuthTokens();
      redirectToLogin();
      return error;
    }

    const data = (await response.json()) as Record<string, unknown>;
    const newAccessToken = data.access_token as string;
    const newRefreshToken = data.refresh_token as string | undefined;

    if (!newAccessToken) {
      clearAuthTokens();
      redirectToLogin();
      return error;
    }

    // Update tokens in auth store via localStorage
    const updatedAuthStore = authStore;
    if (updatedAuthStore.state) {
      updatedAuthStore.state.accessToken = newAccessToken;
      if (newRefreshToken) {
        updatedAuthStore.state.refreshToken = newRefreshToken;
      }
      localStorage.setItem('auth-store', JSON.stringify(updatedAuthStore));
    }

    console.log('[Token Refresh] Token refreshed successfully');

    // Mark error for retry with new token
    // This allows the API client to retry the original request with the updated token
    (error as unknown as Record<string, boolean>).retryAfterRefresh = true;
    return error;
  } catch (err) {
    console.error('[Token Refresh] Token refresh failed:', err);
    // Refresh failed, clear tokens and redirect
    clearAuthTokens();
    redirectToLogin();
    return error;
  }
}

/**
 * Rate Limit Interceptor
 * Handles rate limit responses
 */
export function rateLimitInterceptor(error: Error): Error {
  if (!(error instanceof RateLimitErrorClass)) {
    return error;
  }

  const retryAfter = error.retryAfter || 60;

  console.warn(`Rate limited. Retry after ${retryAfter} seconds`);

  // Could emit event to UI for user feedback
  // EventBus.emit('rate-limited', { retryAfter });

  return error;
}

/**
 * Error Notification Interceptor
 * Shows user-friendly error messages
 */
export function errorNotificationInterceptor(error: Error): Error {
  if (!(error instanceof ApiErrorClass)) {
    return error;
  }

  // Skip logging for specific errors
  if (error instanceof ValidationErrorClass) {
    // Validation errors are typically shown on forms
    return error;
  }

  // Log other errors
  if (import.meta.env.DEV) {
    console.error('[API Error]', {
      status: error.statusCode,
      message: error.message,
      details: error.details,
    });
  }

  return error;
}

/**
 * Helper function to generate unique request ID
 */
function generateRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Helper function to parse Retry-After header
 */
function parseRetryAfterHeader(): number | undefined {
  // In a real implementation, this would parse the header from response
  // For now, return undefined to use default retry logic
  return undefined;
}

/**
 * Helper function to clear authentication tokens
 * Clears Zustand persisted auth store
 */
function clearAuthTokens(): void {
  // Clear Zustand persisted auth store
  localStorage.removeItem('auth-store');

  // Also clear any legacy token storage keys
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('auth_user');
}

/**
 * Helper function to redirect to login
 */
function redirectToLogin(): void {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

/**
 * Helper function to recursively normalize timestamps
 */
function normalizeTimestamps(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(normalizeTimestamps);
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const normalized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (
      typeof value === 'string' &&
      (key.includes('date') || key.includes('time') || key.includes('At') || key.includes('_at'))
    ) {
      // Convert ISO string to Date
      const date = new Date(value);
      normalized[key] = !Number.isNaN(date.getTime()) ? date : value;
    } else if (typeof value === 'object') {
      normalized[key] = normalizeTimestamps(value);
    } else {
      normalized[key] = value;
    }
  }

  return normalized;
}

/**
 * Interceptor factory for adding custom headers
 */
export function createCustomHeaderInterceptor(
  headers: Record<string, string>
): (config: ApiRequestConfig) => ApiRequestConfig {
  return (config: ApiRequestConfig): ApiRequestConfig => ({
    ...config,
    headers: {
      ...config.headers,
      ...headers,
    },
  });
}

/**
 * Interceptor factory for logging
 */
export function createLoggingInterceptor(
  prefix = '[API]'
): (config: ApiRequestConfig) => ApiRequestConfig {
  return (config: ApiRequestConfig): ApiRequestConfig => {
    if (import.meta.env.DEV) {
      console.warn(
        `${prefix} ${config.method || 'GET'} request to ${config.params ? 'with params' : ''}`
      );
    }
    return config;
  };
}
